import { Navigate, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './lib/auth/AuthContext';
import { ProtectedRoute } from './lib/auth/ProtectedRoute';
import { AuthLayout } from './components/layout/AuthLayout';
import { PublicLayout } from './components/layout/PublicLayout';
import {
  ROLES_ADMIN,
  ROLES_GESTIONAN_DATOS,
  ROLES_PUBLICAN_CONTENIDO,
  ROLES_REVISAN_SOLICITUDES,
  ROLES_VEN_REPROGRAMACION,
  ROLES_VEN_SUSPENSIONES,
} from './lib/auth/roles';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import { SinPermiso } from './pages/SinPermiso';
import { SolicitarAcceso } from './pages/SolicitarAcceso';
import { VecinoHome } from './pages/vecino/VecinoHome';
import { EstadoCuenta } from './pages/vecino/EstadoCuenta';
import { MisDatos } from './pages/vecino/MisDatos';
import { MiHistorial } from './pages/vecino/MiHistorial';
import { WizardOnboarding } from './pages/vecino/WizardOnboarding';
import { Documentos } from './pages/vecino/Documentos';
import { Avisos } from './pages/vecino/Avisos';
import { Ayuda } from './pages/Ayuda';
import { SinImplementar } from './pages/SinImplementar';
import { ColaSolicitudes } from './pages/admin/ColaSolicitudes';
import { Validaciones } from './pages/admin/Validaciones';
import { Suspensiones } from './pages/admin/Suspensiones';
import { Reprogramacion } from './pages/admin/Reprogramacion';
import { DocumentosPage } from './pages/admin/Documentos';
import { AvisosPage } from './pages/admin/Avisos';
import { TenenciasPage } from './pages/admin/Tenencias';
import { ConsultaVecinos } from './pages/admin/ConsultaVecinos';
import { Eliminaciones } from './pages/admin/Eliminaciones';
import { AyudaComision } from './pages/admin/AyudaComision';
import { Privacidad } from './pages/Privacidad';
import { TesoreriaLayout } from './components/layout/TesoreriaLayout';
import { TesoreriaHome } from './pages/tesoreria/TesoreriaHome';
import { PagosPage } from './pages/tesoreria/Pagos';
import { CompensacionesPage } from './pages/tesoreria/Compensaciones';
import { RecursosPage } from './pages/tesoreria/Recursos';
import { BancoPage } from './pages/tesoreria/Banco';
import { CasasPage } from './pages/tesoreria/Casas';
import { IngresosVariosPage } from './pages/tesoreria/IngresosVarios';
import { GastosPage } from './pages/tesoreria/Gastos';
import { CuotasPage } from './pages/tesoreria/Cuotas';
import { ReportesPage } from './pages/tesoreria/Reportes';
import { EntesExternosPage as TesoreriaEntesExternosPage } from './pages/tesoreria/EntesExternos';
// Sprint 14 S14-06b — Showcase del design system. Gate por
// import.meta.env.DEV (convención #15 corolario en CLAUDE.md). NO accesible
// en producción: `pnpm build` resuelve DEV=false → la ruta no se monta y
// el bundler hace tree-shake del condicional.
import { Showcase } from './pages/dev/Showcase';
// Sprint 14 S14-07 — Preview del Dashboard sin auth para review visual.
import { DashboardPreview } from './pages/dev/DashboardPreview';
// Sprint 14 S14-10 — Preview del shell completo (Header + BottomNav +
// Footer) sin auth, para mobile audit en DevTools.
import { ShellPreview } from './pages/dev/ShellPreview';
// Sprint 14 S14-11 — Preview de Mis Datos con mock data + escenarios.
import { MisDatosPreview } from './pages/dev/MisDatosPreview';
// Smoke Fase 8 — Preview del rediseño de "Recursos de tu casa".
import { MisRecursosPreview } from './pages/dev/MisRecursosPreview';
import { EstadoCuentaPreview } from './pages/dev/EstadoCuentaPreview';
// Sprint 15 S15-01 — Preview del modal "Datos para transferir".
import { DatosCuentaPreview } from './pages/dev/DatosCuentaPreview';
// Sprint 15 S15-Z — Preview del mantenedor /tesoreria/recursos.
import { RecursosPreview } from './pages/dev/RecursosPreview';
// Sprint 17 Fase 2 — Preview del dashboard de Tesorería.
import { TesoreriaHomePreview } from './pages/dev/TesoreriaHomePreview';
// Sprint 17 Fase 2 — Preview del control de saldo bancario.
import { TesoreriaBancoPreview } from './pages/dev/TesoreriaBancoPreview';
// Sprint 17 Fase 2 — Preview de Casas.
import { TesoreriaCasasPreview } from './pages/dev/TesoreriaCasasPreview';
// Sprint 17 Fase 2 — Preview de Pagos.
import { TesoreriaPagosPreview } from './pages/dev/TesoreriaPagosPreview';
// Sprint 17 Fase 2 — Preview de Ingresos varios.
import { TesoreriaIngresosVariosPreview } from './pages/dev/TesoreriaIngresosVariosPreview';
// Sprint 17 Fase 2 — Preview de Gastos.
import { TesoreriaGastosPreview } from './pages/dev/TesoreriaGastosPreview';
// Sprint 17 Fase 2 — Preview de Compensaciones.
import { TesoreriaCompensacionesPreview } from './pages/dev/TesoreriaCompensacionesPreview';
// Sprint 19a — Preview de Suspensiones-rediseño (smoke dev-only).
import { SuspensionesPreview } from './pages/dev/SuspensionesPreview';
import { ReprogramacionPreview } from './pages/dev/ReprogramacionPreview';
// Sprint 19a RP-04 — Preview del tab Pilas (venta en un solo paso).
import { PilasPreview } from './pages/dev/PilasPreview';
import { TesoreriaReportesPreview } from './pages/dev/TesoreriaReportesPreview';
// Consulta de vecinos (módulo Comisión) — preview dev-only.
import { ConsultaVecinosPreview } from './pages/dev/ConsultaVecinosPreview';
import { AccesoWifiPreview } from './pages/dev/AccesoWifiPreview';
import { AccesoWifiPage } from './pages/admin/AccesoWifi';

function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Sprint 14 S14-06b / S14-07 — review visual dev-only. */}
        {import.meta.env.DEV && (
          <>
            <Route path="/dev/showcase" element={<Showcase />} />
            <Route path="/dev/dashboard" element={<DashboardPreview />} />
            <Route path="/dev/shell" element={<ShellPreview />} />
            <Route path="/dev/mis-datos" element={<MisDatosPreview />} />
            <Route path="/dev/mis-recursos" element={<MisRecursosPreview />} />
            {/* F14: revisar la Ayuda sin login (es ruta autenticada). */}
            <Route path="/dev/ayuda" element={<Ayuda />} />
            <Route path="/dev/ayuda-comision" element={<AyudaComision />} />
            <Route path="/dev/estado-cuenta" element={<EstadoCuentaPreview />} />
            <Route path="/dev/datos-cuenta" element={<DatosCuentaPreview />} />
            <Route path="/dev/recursos" element={<RecursosPreview />} />
            <Route
              path="/dev/tesoreria-home"
              element={<TesoreriaHomePreview />}
            />
            <Route
              path="/dev/tesoreria-banco"
              element={<TesoreriaBancoPreview />}
            />
            <Route
              path="/dev/tesoreria-casas"
              element={<TesoreriaCasasPreview />}
            />
            <Route
              path="/dev/tesoreria-pagos"
              element={<TesoreriaPagosPreview />}
            />
            <Route
              path="/dev/tesoreria-ingresos-varios"
              element={<TesoreriaIngresosVariosPreview />}
            />
            <Route
              path="/dev/tesoreria-gastos"
              element={<TesoreriaGastosPreview />}
            />
            <Route
              path="/dev/tesoreria-compensaciones"
              element={<TesoreriaCompensacionesPreview />}
            />
            <Route
              path="/dev/suspensiones"
              element={<SuspensionesPreview />}
            />
            <Route
              path="/dev/reprogramacion"
              element={<ReprogramacionPreview />}
            />
            <Route path="/dev/pilas" element={<PilasPreview />} />
            <Route
              path="/dev/tesoreria-reportes"
              element={<TesoreriaReportesPreview />}
            />
            <Route
              path="/dev/consulta-vecinos"
              element={<ConsultaVecinosPreview />}
            />
            <Route
              path="/dev/acceso-wifi"
              element={<AccesoWifiPreview />}
            />
          </>
        )}

        {/* Rutas públicas con PublicLayout (Sprint 11 v2 S11-V2-03) */}
        <Route element={<PublicLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/sin-permiso" element={<SinPermiso />} />
          <Route path="/solicitar-acceso" element={<SolicitarAcceso />} />
        </Route>

        {/* Política de privacidad (Ley 21.719, P10) — accesible siempre. */}
        <Route path="/privacidad" element={<Privacidad />} />

        {/* Rutas autenticadas con AuthLayout (vista vecino + comisión) */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AuthLayout />}>
            <Route path="/" element={<Navigate to="/vecino" replace />} />

            <Route path="/vecino" element={<VecinoHome />} />
            <Route path="/vecino/estado-cuenta" element={<EstadoCuenta />} />
            {/* /vecino/historial fue tab interno de /vecino/estado-cuenta
                en el refactor S14-Historial v2. Redirige para no romper
                enlaces guardados o emails antiguos. */}
            <Route
              path="/vecino/historial"
              element={<Navigate to="/vecino/estado-cuenta" replace />}
            />
            {/* /vecino/mi-casa se consolido en /vecino/mis-datos (S13-32).
                Redirige para no romper enlaces guardados. */}
            <Route
              path="/vecino/mi-casa"
              element={<Navigate to="/vecino/mis-datos" replace />}
            />
            <Route path="/vecino/mis-datos" element={<MisDatos />} />
            <Route path="/vecino/mi-historial" element={<MiHistorial />} />
            {/* Sprint 18 P2-B3: wizard de onboarding activo en producción.
                ProtectedRoute redirige aquí a las tenencias ocupantes con el
                wizard sin completar. */}
            <Route path="/vecino/onboarding" element={<WizardOnboarding />} />
            <Route path="/vecino/documentos" element={<Documentos />} />
            <Route path="/vecino/avisos" element={<Avisos />} />
            <Route path="/ayuda" element={<Ayuda />} />
            <Route path="/sin-implementar" element={<SinImplementar />} />

            <Route element={<ProtectedRoute requireRole={ROLES_ADMIN} />}>
              <Route path="/admin" element={<Navigate to="/admin/solicitudes-acceso" replace />} />
              <Route element={<ProtectedRoute requireRole={ROLES_REVISAN_SOLICITUDES} />}>
                <Route path="/admin/solicitudes-acceso" element={<ColaSolicitudes />} />
                <Route path="/admin/validaciones" element={<Validaciones />} />
              </Route>
              {/* Sprint 19a: comisión y tesorero LEEN (read-only la comisión);
                  los botones de transición se gatean dentro con ROLES_SUSPENDEN. */}
              <Route element={<ProtectedRoute requireRole={ROLES_VEN_SUSPENSIONES} />}>
                <Route path="/admin/suspensiones" element={<Suspensiones />} />
              </Route>
              {/* S21: Reprogramación de accesos (origen distinto a Suspensiones:
                  cambio de dato del vecino, no morosidad). Comisión lee,
                  tesorero opera (gate por vista dentro del componente). */}
              <Route element={<ProtectedRoute requireRole={ROLES_VEN_REPROGRAMACION} />}>
                <Route path="/admin/reprogramacion" element={<Reprogramacion />} />
              </Route>
              <Route element={<ProtectedRoute requireRole={ROLES_PUBLICAN_CONTENIDO} />}>
                <Route path="/admin/documentos" element={<DocumentosPage />} />
                <Route path="/admin/avisos" element={<AvisosPage />} />
              </Route>
              {/*
                /admin/tenencias (cerrar tenencia — flujo ex-vecino):
                rol ROLES_ADMIN (comision o tesorero). Sprint 13 S13-07.
              */}
              <Route path="/admin/tenencias" element={<TenenciasPage />} />
              {/* Consulta de vecinos (módulo Comisión): buscar por patente /
                  RUT / nombre / recurso → ficha de la casa. Solo lectura.
                  Backend require_comision; aquí basta el gate ROLES_ADMIN
                  porque el tesorero siempre tiene rol comisión (doctrina #14). */}
              <Route
                path="/admin/consulta-vecinos"
                element={<ConsultaVecinos />}
              />
              {/* Reportes de tesorería en lectura para la comisión (mismo
                  componente que /tesoreria/reportes; el backend ya autoriza a
                  comisión vía require_comision_o_tesorero). Patrón Suspensiones:
                  un componente, dos rutas/layouts. */}
              <Route path="/admin/reportes" element={<ReportesPage />} />
              {/* Ayuda de la vista Comisión (guía por página). */}
              <Route path="/admin/ayuda" element={<AyudaComision />} />
              {/* Cola de eliminación de cuenta (Ley 21.719, P10). Solo
                  comisión (coherente con require_comision del backend). */}
              <Route element={<ProtectedRoute requireRole={ROLES_GESTIONAN_DATOS} />}>
                <Route path="/admin/eliminaciones" element={<Eliminaciones />} />
              </Route>
            </Route>
          </Route>
        </Route>

        {/*
          Vista Tesorería con sub-Layout propio (Sprint 13 S13-08).
          Sidebar + header distintos del portal vecino. Mismo dominio,
          misma SPA, misma sesión.
        */}
        <Route element={<ProtectedRoute requireRole={['tesorero']} />}>
          <Route element={<TesoreriaLayout />}>
            <Route path="/tesoreria" element={<TesoreriaHome />} />
            <Route path="/tesoreria/pagos" element={<PagosPage />} />
            <Route
              path="/tesoreria/compensaciones"
              element={<CompensacionesPage />}
            />
            <Route path="/tesoreria/recursos" element={<RecursosPage />} />
            <Route
              path="/tesoreria/entes-externos"
              element={<TesoreriaEntesExternosPage />}
            />
            <Route path="/tesoreria/banco" element={<BancoPage />} />
            <Route path="/tesoreria/casas" element={<CasasPage />} />
            <Route
              path="/tesoreria/ingresos-varios"
              element={<IngresosVariosPage />}
            />
            <Route path="/tesoreria/gastos" element={<GastosPage />} />
            <Route path="/tesoreria/cuotas" element={<CuotasPage />} />
            {/* Sprint 19a: hogar de Suspensiones en Tesorería (el tesorero la
                opera en su layout). La comisión la lee desde /admin/suspensiones
                — mismo componente, su propio layout. */}
            <Route path="/tesoreria/suspensiones" element={<Suspensiones />} />
            {/* S21: el tesorero opera la reprogramación desde su layout. */}
            <Route
              path="/tesoreria/reprogramacion"
              element={<Reprogramacion />}
            />
            {/* Operación de accesos wifi contra eWeLink (F5). */}
            <Route
              path="/tesoreria/acceso-wifi"
              element={<AccesoWifiPage />}
            />
            <Route path="/tesoreria/reportes" element={<ReportesPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
